/**
 * Application locale configuration.
 *
 * Two distinct locales are tracked, mirroring the Windows "Language" vs
 * "Date, time, and number format (Region)" split:
 *
 *  - The **language locale** follows the display Language preference and drives the
 *    `Accept-Language` header (server-side localization / ABP texts). See getLanguageLocale().
 *  - The **format locale** drives all client-side date/time/number formatting and is set from
 *    the dedicated `Nexo.User.FormatLocale` preference, falling back to the language locale when
 *    unset. See getActiveLocale().
 */

const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  sv: 'sv-SE',
};

const DEFAULT_LOCALE = 'en-US';

/** Map a language code (e.g. 'en', 'sv') to a BCP 47 locale tag. */
export function languageToLocale(language: string): string {
  return LOCALE_MAP[language] ?? DEFAULT_LOCALE;
}

// Read persisted preferences immediately at module load time so that the initial render uses
// the correct locales before UserPreferencesContext has finished fetching from the server.
const _storedLang = typeof localStorage !== 'undefined' ? localStorage.getItem('nexo-language') : null;
const _storedFormat = typeof localStorage !== 'undefined' ? localStorage.getItem('nexo-format-locale') : null;

/**
 * Mirror the language locale into ASP.NET's culture cookie. The cookie culture provider outranks
 * Accept-Language, and the ABP account pages (same host — cookies ignore ports, and /api is
 * proxied same-origin) set this cookie at login: a stale value there silently forces every
 * server-localized text (ABP texts inside DTOs) into another language. Overwriting it keeps
 * server-side localization deterministically on the app's display language.
 */
function syncCultureCookie(localeTag: string): void {
  if (typeof document === 'undefined') return;
  const value = encodeURIComponent(`c=${localeTag}|uic=${localeTag}`);
  document.cookie = `.AspNetCore.Culture=${value}; path=/; max-age=31536000; SameSite=Lax`;
}

// Language locale: drives Accept-Language. Follows the display Language preference.
let _languageLocale = _storedLang ? languageToLocale(_storedLang) : DEFAULT_LOCALE;
if (_storedLang) {
  syncCultureCookie(_languageLocale);
}
// Format locale: drives date/time/number formatting. The persisted format pref wins; otherwise
// it follows the language locale.
let _activeLocale = _storedFormat || _languageLocale;

/**
 * Set the language locale from a display-language code (e.g. 'en', 'sv'). Drives Accept-Language.
 * Does not change number/date formatting on its own — call setActiveLocaleTag for that.
 */
export function setActiveLocale(language: string): void {
  _languageLocale = languageToLocale(language);
  syncCultureCookie(_languageLocale);
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('nexo-language', language);
  }
}

/**
 * Set the format locale directly from a BCP 47 tag (e.g. 'sv-SE'). Governs all date/time/number
 * formatting. Pass an empty string to follow the current language locale ("same as language").
 */
export function setActiveLocaleTag(localeTag: string): void {
  _activeLocale = localeTag || _languageLocale;
  if (typeof localStorage !== 'undefined') {
    if (localeTag) {
      localStorage.setItem('nexo-format-locale', localeTag);
    } else {
      localStorage.removeItem('nexo-format-locale');
    }
  }
}

/** Get the active BCP 47 format locale tag (date/time/number formatting). */
export function getActiveLocale(): string {
  return _activeLocale;
}

/** Get the BCP 47 language locale tag (Accept-Language / server localization). */
export function getLanguageLocale(): string {
  return _languageLocale;
}

/**
 * @deprecated Use getActiveLocale() instead. Kept for backward compatibility with generated grids.
 */
export const APP_LOCALE = DEFAULT_LOCALE;

export function formatDate(date: Date): string {
  return date.toLocaleDateString(_activeLocale);
}

export function formatDateTime(date: Date): string {
  return date.toLocaleString(_activeLocale);
}

/**
 * Format a number using the active format locale (thousands grouping + locale decimal separator).
 * Returns '' for null/undefined/NaN. Intl.NumberFormat instances are cached per (locale, opts).
 */
export function formatNumber(
  value: number | null | undefined,
  opts?: Intl.NumberFormatOptions,
): string {
  if (value == null || Number.isNaN(value)) return '';
  return getNumberFormat(_activeLocale, opts).format(value);
}

/**
 * Parse a user-typed number string according to the active format locale's group/decimal
 * separators (e.g. sv-SE "1 039 000,38" → 1039000.38, en-US "1,039,000.38" → 1039000.38).
 *
 * Lenient: when the locale's decimal separator is not '.', a typed '.' is still accepted as the
 * decimal point as long as the locale separator is absent — so sv users can type either
 * "1,25" or "1.25". Returns NaN for blank/invalid input. Numbers pass through unchanged.
 */
export function parseLocaleNumber(text: string | number | null | undefined): number {
  if (text == null) return NaN;
  if (typeof text === 'number') return text;
  let s = text.trim();
  if (s === '') return NaN;
  const parts = new Intl.NumberFormat(_activeLocale).formatToParts(1234567.8);
  const group = parts.find((p) => p.type === 'group')?.value ?? '';
  const decimal = parts.find((p) => p.type === 'decimal')?.value ?? '.';
  // Strip all whitespace (covers NBSP / narrow-NBSP group separators) and the locale group char.
  s = s.replace(/\s/g, '');
  if (group && group.trim() !== '') s = s.split(group).join('');
  // Normalise the locale decimal separator to '.'.
  if (decimal !== '.' && s.includes(decimal)) {
    s = s.split(decimal).join('.');
  }
  // Keep only valid numeric characters.
  s = s.replace(/[^0-9.+-]/g, '');
  if (s === '' || s === '-' || s === '+' || s === '.') return NaN;
  return Number(s);
}

/**
 * Build a MUI DataGrid `valueFormatter` that renders finite numbers via the active format
 * locale and leaves blanks/non-numbers empty. Reusable across grids (and the grid generator).
 */
export function numberValueFormatter(opts?: Intl.NumberFormatOptions) {
  return (value: unknown): string =>
    typeof value === 'number' && Number.isFinite(value) ? formatNumber(value, opts) : '';
}

const _numberFormatCache = new Map<string, Intl.NumberFormat>();

function getNumberFormat(locale: string, opts?: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = locale + '|' + (opts ? JSON.stringify(opts) : '');
  let fmt = _numberFormatCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, opts);
    _numberFormatCache.set(key, fmt);
  }
  return fmt;
}
